import { memo, useEffect, useMemo } from "react"
import type { FC } from "react"

import type { DevicePlotDef } from "types/dashboard.types"

import useDateRange from "helpers/hooks/useDateRange"
import { getCellHeight } from "helpers/utils/dashboards"
import type { EntityType } from "types/common.types"
import { useMultiDeviceTelemetry } from "helpers/hooks/plots/useMultiDeviceTelemetry"
import { usePlotProcessor } from "helpers/hooks/plots/usePlotProcessor"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import Plot from "../Plot"

const PlotMemo = memo(Plot)

interface DeviceTelemetryPlotProps {
  def: DevicePlotDef
  deviceIds: number[]
}

const DeviceTelemetryPlot: FC<DeviceTelemetryPlotProps> = ({ def, deviceIds }) => {
  const { view, reqConfig } = def
  const { config, plotLayout, dispatchPlotLayout, entityType } = useDashboardContext()

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools(entityType ?? "device"),
    [getDateRangeTools, entityType],
  )

  const { data, qualityData, qualityError, isRawData, error, isFetching, refetch } =
    useMultiDeviceTelemetry({
      deviceIds,
      reqConfig,
      dateRange,
      isDateRangeValid,
    })

  const {
    formattedDef,
    tabLegend,
    fieldQualityMap,
    plotData,
    tooltipData,
    shouldRender,
  } = usePlotProcessor({
    data,
    def,
    entityIds: deviceIds,
    isRawData,
    dateRange,
    error,
  })

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: "device",
      entityIds: deviceIds,
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [deviceIds, dateRange?.fromDate, dateRange?.toDate],
  )

  useEffect(() => {
    if (dispatchPlotLayout && config) {
      dispatchPlotLayout({
        type: "UPDATE_CELL_HEIGHT",
        payload: {
          id: view.nameKey,
          h: shouldRender ? getCellHeight(view.nameKey, config.type) : 0,
        },
      })
    }
  }, [shouldRender, view.nameKey, dispatchPlotLayout, config])

  const render = Boolean(
    shouldRender && (plotLayout === null || plotLayout?.[view.nameKey]?.h !== 0),
  )

  return (
    <>
      {render && (
        <PlotMemo
          data={plotData}
          def={formattedDef}
          csvData={csvData}
          tooltipData={tooltipData}
          entityData={{ entity: "device", ids: deviceIds }}
          qualityData={qualityData}
          qualityError={qualityError}
          isRawData={isRawData}
          seriesLegend={tabLegend}
          fieldQualityMap={fieldQualityMap}
          error={error}
          isFetching={isFetching}
          refetch={refetch}
        />
      )}
    </>
  )
}

export default DeviceTelemetryPlot
